// viewCards.js
// A View is a set of CARDS: each widget instance runs one function with its own
// filters. A run belongs to a card when it was executed FOR that card —
// by the card's Run button or by the View-managed automation configured for
// it — and both stamp the same `view_id` / `view_instance_id` pair onto the
// run's `trigger_context` (run_data_function for a manual run, the two
// dispatchers for an automation's). Hydration and the live repaint adopt runs
// by that identity alone: a colleague's Dashboard run of the same function,
// with the same filters or different ones, never moves a card.
//
// Because an automation may run with dynamic dates, the run a card adopts can
// carry parameters its rail was not showing; `controlsFromRunParameters` maps
// the run's parameters back onto the rail's controls so the controls always
// describe the figures on screen.

import { isDateRangeParam } from './functionParameterUtils';

export const runBelongsToCard = (run, viewId, instanceId) => {
  const context = run?.trigger_context;
  if (!context || !instanceId) return false;
  if (context.view_instance_id !== instanceId) return false;
  return !viewId || context.view_id === viewId;
};

// The rail's controls for one card, seeded from the parameters a run was
// executed with (the request shape: `{ [block_id]: { [column]: values,
// exclude_filters, range_filters } }`). Mirrors ViewDisplay's
// buildInstanceControls, which seeds them from the version's defaults. Only
// the version's variable filters become controls; a run parameter the version
// no longer declares is ignored.
export const controlsFromRunParameters = (dataSourceBlocks, runParameters) => {
  const functionParams = {};
  const excludedColumns = {};
  Object.entries(dataSourceBlocks || {}).forEach(([blockId, block]) => {
    const blockParams = {};
    const ran = runParameters?.[blockId] || {};
    (block.filters || []).forEach((filter) => {
      if (!filter.variable) return;
      const column = filter.column;
      if (filter.range) {
        const range = ran.range_filters?.[column];
        blockParams[column] = { gte: range?.gte || '', lte: range?.lte || '' };
        return;
      }
      if (isDateRangeParam(column)) {
        const value = Array.isArray(ran[column]) ? ran[column][0] : ran[column];
        blockParams[column] = value === null || value === undefined ? '' : String(value);
        return;
      }
      // The rail models every other variable filter as a list of values, so a
      // run parameter recorded as a single value is wrapped rather than read
      // as "no filter".
      const asList = (value) => {
        if (Array.isArray(value)) return value;
        return value === null || value === undefined || value === '' ? [] : [value];
      };
      const excluded = ran.exclude_filters?.[column];
      if (excluded !== undefined && excluded !== null) {
        blockParams[column] = asList(excluded);
        excludedColumns[`${blockId}::${column}`] = true;
        return;
      }
      blockParams[column] = asList(ran[column]);
    });
    if (Object.keys(blockParams).length > 0) {
      functionParams[blockId] = {
        name: block.name || blockId,
        data: blockParams,
        tableId: block.table_id,
      };
    }
  });
  return { functionParams, excludedColumns };
};

// What each card is called where cards are listed by name: the function's
// name, with an ordinal (in card order) only where a function appears on more
// than one card.
export const cardLabels = (cards) => {
  const counts = {};
  (cards || []).forEach((card) => {
    counts[card.functionName] = (counts[card.functionName] || 0) + 1;
  });
  const seen = {};
  const labels = {};
  (cards || []).forEach((card) => {
    seen[card.functionName] = (seen[card.functionName] || 0) + 1;
    labels[card.instanceId] = counts[card.functionName] > 1
      ? `${card.functionName} (${seen[card.functionName]})`
      : card.functionName;
  });
  return labels;
};
